import { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import GameCanvas from './game/GameCanvas';
import Sidebar from './components/Sidebar';
import { useWebSocket } from './api/websocket';

interface Resources {
  fuel: number;
  steel: number;
  ammo: number;
  capFuel: number;
  capSteel: number;
  capAmmo: number;
}

interface BuildingType {
  id: string;
  name: string;
  description: string;
  icon: string;
  costSteel: number;
  costAmmo: number;
  prodFuel: number;
  prodSteel: number;
  prodAmmo: number;
  maxLevel: number;
}

interface PlayerBuilding {
  id: string;
  buildingType: string;
  level: number;
  gridX: number;
  gridY: number;
}

const API = '/api/game';
const DEMO_PLAYER = 'demo-player-001';

async function api(path: string, opts?: RequestInit) {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'API Fehler');
  }
  return res.json();
}

export default function App() {
  const [connected, setConnected] = useState(false);
  const [resources, setResources] = useState<Resources>({ fuel: 10, steel: 0, ammo: 0, capFuel: 100, capSteel: 100, capAmmo: 100 });
  const [buildingTypes, setBuildingTypes] = useState<BuildingType[]>([]);
  const [buildings, setBuildings] = useState<PlayerBuilding[]>([]);
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const { socket } = useWebSocket();
  const [placing, setPlacing] = useState<string | null>(null);

  // ── State laden ─────────────────────────────────
  const loadState = useCallback(async () => {
    try {
      const data = await api('/state');
      setResources(data.resources);
      setBuildings(data.buildings);
      setBuildingTypes(data.buildingTypes);
    } catch (err) {
      console.error('State laden fehlgeschlagen:', err);
    }
  }, []);

  useEffect(() => { loadState(); }, [loadState]);

  // WebSocket events
  useEffect(() => {
    if (!socket) return;
    socket.on('connect', () => { setConnected(true); loadState(); });
    socket.on('disconnect', () => setConnected(false));
    socket.on('game:state', (data: any) => {
      if (data.resources) setResources(data.resources);
      if (data.buildings) setBuildings(data.buildings);
    });
    return () => { socket.off('connect'); socket.off('disconnect'); socket.off('game:state'); };
  }, [socket, loadState]);

  // ── Bauen ──────────────────────────────────────
  const handleBuild = useCallback(async (buildingType: string) => {
    try {
      // Find next free grid position
      const occupied = new Set(buildings.map(b => `${b.gridX},${b.gridY}`));
      let gx = 0, gy = 0;
      for (let y = 0; y < 10; y++) {
        for (let x = 0; x < 10; x++) {
          if (!occupied.has(`${x},${y}`)) { gx = x; gy = y; break; }
        }
        if (gx !== 0 || gy !== 0) break;
      }

      const data = await api('/build', {
        method: 'POST',
        body: JSON.stringify({ buildingType, gridX: gx, gridY: gy }),
      });
      setResources(data.resources);
      setBuildings(prev => [...prev, data.building]);
    } catch (err: any) {
      console.error('Bauen fehlgeschlagen:', err);
    }
  }, [buildings]);

  // ── Upgraden ───────────────────────────────────
  const handleUpgrade = useCallback(async (buildingId: string) => {
    try {
      const data = await api('/upgrade', {
        method: 'POST',
        body: JSON.stringify({ buildingId }),
      });
      setResources(data.resources);
      setBuildings(prev => prev.map(b => b.id === buildingId ? data.building : b));
    } catch (err: any) {
      console.error('Upgrade fehlgeschlagen:', err);
    }
  }, []);

  return (
    <div className="app">
      <Header connected={connected} fuel={resources.fuel} steel={resources.steel} ammo={resources.ammo} />
      <main className="main-content">
        <GameCanvas selectedUnit={selectedUnit} buildings={buildings} />
        <Sidebar
          selectedUnit={selectedUnit}
          onSelectUnit={setSelectedUnit}
          resources={resources}
          buildingTypes={buildingTypes}
          onBuild={handleBuild}
          onUpgrade={handleUpgrade}
          buildings={buildings}
        />
      </main>
    </div>
  );
}
